import { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, Video, Camera, CreditCard, MessageCircle } from 'lucide-react';

export default function AntiScamBanner() {
  const [isExpanded, setIsExpanded] = useState(false);

  const policies = [
    {
      icon: MessageCircle,
      title: "No Upfront Payment",
      description: "We never require deposits or upfront payments. You pay only when you pick up your kitten in person."
    },
    {
      icon: Video,
      title: "Live Video Verification",
      description: "We gladly invite all prospective families to schedule a live video call (via FaceTime, Zoom, or WhatsApp) to meet us, see our home environment, and interact with the kittens and their parents in real time."
    },
    {
      icon: Camera,
      title: "Custom Proof-of-Life Photos",
      description: "If you want extra peace of mind before a call, we will happily send you a real-time photo of your chosen kitten sitting next to a handwritten note featuring your name and today's date."
    },
    {
      icon: CreditCard,
      title: "Safe Payments Only",
      description: "We accept payment only at pickup. We prioritize secure, in-person transactions for your protection and peace of mind."
    }
  ];

  return (
    <div className="bg-gradient-to-r from-rose-50/80 to-pink-50/80 border-b border-rose-100/50">
      {/* Collapsed View - More compact on mobile */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 lg:py-2.5 flex items-center justify-center gap-1.5 lg:gap-2 hover:bg-rose-50/50 transition-colors"
        aria-expanded={isExpanded}
        data-testid="anti-scam-banner-toggle"
      >
        <Shield className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#E85D75]" />
        <span className="text-xs lg:text-sm font-medium text-stone-700">
          Our Commitment to Transparency
        </span>
        <span className="text-[10px] lg:text-xs text-[#E85D75] hidden sm:inline">
          — We believe in 100% transparency
        </span>
        {isExpanded ? (
          <ChevronUp className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#E85D75] ml-0.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#E85D75] ml-0.5" />
        )}
      </button>

      {/* Expanded Content with Animation */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 pt-2" data-testid="anti-scam-banner-content">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs lg:text-sm text-stone-600 mb-3 lg:mb-4 text-center leading-relaxed">
              We know that finding a kitten online can be stressful due to the rise of internet pet scams. 
              At KittenState, we believe in 100% transparency and the safety of our future pet parents.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-3">
              {policies.map((policy, index) => (
                <div 
                  key={index}
                  className="flex gap-2.5 lg:gap-3 p-2.5 lg:p-3 bg-white/70 rounded-xl border border-rose-100/50"
                >
                  <div className="flex-shrink-0 w-7 h-7 lg:w-8 lg:h-8 bg-rose-100/80 rounded-full flex items-center justify-center">
                    <policy.icon className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#E85D75]" />
                  </div>
                  <div>
                    <h4 className="text-xs lg:text-sm font-semibold text-stone-800">{policy.title}</h4>
                    <p className="text-[11px] lg:text-xs text-stone-600 mt-0.5 leading-relaxed">{policy.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
